import React from 'react';
import { ShoppingCart, CheckCircle, Star, MonitorSmartphone } from 'lucide-react';
import { UserCountDisplay } from './icons/UsersIcon';

const OptimizedProductCard = ({ 
 product, 
 selectedDuration, 
 onProductClick, 
 className = "" 
}) => {
 const pricing = product.pricing[selectedDuration];
 const monthlyPrice = selectedDuration > 1 ? (pricing.price / selectedDuration).toFixed(2) : null;
 const savings = (pricing.originalPrice - pricing.price).toFixed(2);

 return (
 <div
 className={`group relative bg-white rounded-xl shadow-lg border border-ink-200 p-8 cursor-pointer 
 hover:shadow-md hover:border-ink-400 
 transition-all duration-300 ease-out overflow-hidden ${className}`}
 onClick={() => onProductClick(product)}
 >
 {/* 微妙的装饰性背景 - 极简设计 */}
 <div className="absolute -top-20 -right-20 w-40 h-40 bg-cream-100 rounded-full opacity-30 blur-3xl"></div>
 <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-cream-100 rounded-full opacity-30 blur-3xl"></div>
 
 <div className="relative z-10">
 {/* 产品头部 - 精简设计 */}
 <div className="flex items-start justify-between mb-8">
 <div className="flex items-center gap-4">
 <div className="w-16 h-16 bg-gradient-to-br from-gray-800 to-black rounded-2xl flex items-center justify-center text-white shadow-xl transition-transform duration-300">
 {product.icon}
 </div>
 <div>
 <h3 className="text-2xl font-bold text-ink-900 mb-2 group-hover:text-black transition-colors">
 {product.name}
 </h3>
 <p className="text-ink-600 leading-relaxed">{product.description}</p>
 </div>
 </div>
 
 {/* 用户数量显示 - 简约版 */}
 <div className="flex flex-col items-center">
 <UserCountDisplay count={product.users} size="sm" />
 </div>
 </div>

 {/* 价格区域 - 专业营销设计 */}
 <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-ink-100 shadow-inner mb-8">
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-baseline gap-3">
 <span className="text-4xl font-bold text-black">
 ${pricing.price}
 </span>
 <span className="text-xl text-ink-400 line-through">
 ${pricing.originalPrice}
 </span>
 </div>
 <div className="bg-black text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
 省 ${savings}
 </div>
 </div>
 
 {/* 月均价格 - 高端展示 */}
 {monthlyPrice && (
 <div className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-800 to-black text-white px-4 py-2 rounded-full shadow-lg">
 <Star className="w-4 h-4" />
 <span className="text-sm font-bold">月均 ${monthlyPrice}</span>
 </div>
 )}
 </div>

 {/* 产品特性 - 清晰列表 */}
 <div className="space-y-4 mb-8">
 {product.features.map((feature, idx) => (
 <div key={idx} className="flex items-center gap-3 text-ink-700 group-hover:text-ink-800 transition-colors">
 <div className="w-5 h-5 bg-black rounded-full flex items-center justify-center flex-shrink-0">
 <CheckCircle className="w-3 h-3 text-white" />
 </div>
 <span className="font-medium leading-relaxed">{feature}</span>
 </div>
 ))}
 </div>

 {/* 产品亮点标签 - 简约标签系统 */}
 <div className="flex flex-wrap gap-2 mb-8">
 {product.highlights.map((highlight, idx) => (
 <span 
 key={idx} 
 className="inline-flex items-center bg-cream-100 text-ink-800 px-3 py-1.5 rounded-full text-sm font-medium border border-ink-200 hover:bg-ink-100 transition-colors"
 >
 {highlight}
 </span>
 ))}
 </div>

 {/* CTA按钮 - 专业简约设计 */}
 <button 
 onClick={(e) => {
 e.stopPropagation();
 onProductClick(product);
 }}
 className="relative w-full bg-black hover:bg-ink-800 text-white py-4 rounded-2xl font-bold text-lg shadow-lg flex items-center justify-center gap-3 transition-all duration-300 group/btn overflow-hidden"
 >
 {/* 微妙的hover效果 */}
 <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-black opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
 
 {/* 按钮内容 */}
 <div className="relative z-10 flex items-center gap-3">
 <ShoppingCart className="w-5 h-5 group-hover/btn:rotate-12 transition-transform duration-300" />
 <span>立即购买</span>
 <span className="text-xl group-hover/btn:translate-x-1 transition-transform duration-300">→</span>
 </div>
 </button>

 {/* 支持环境显示 - 极简信息 */}
 <div className="flex items-center justify-center gap-2 mt-6 text-sm text-ink-500">
 <MonitorSmartphone className="w-4 h-4 text-ink-400" />
 <span>支持网页+APP使用</span>
 </div>
 </div>

 {/* 悬停时的边框光效 - 极简版 */}
 <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
 <div className="absolute inset-0 rounded-xl border-2 border-black/10"></div>
 </div>
 </div>
 );
};

export default OptimizedProductCard;